import React, { Fragment, useState, useEffect, useMemo } from "react";

import Loader from "../Loader";

import { useAlert } from "react-alert";
import { useDispatch, useSelector } from "react-redux";

import { updatePassword, clearErrors } from "../../actions/userActions";
import { UPDATE_PASSWORD_RESET } from "./../../constants/userConstants";
import "./UpdatePassword.css";

const PASSWORD_REQUIREMENTS = [
  {
    key: "length",
    label: "At least 8 characters long",
    test: (value) => value.length >= 8,
  },
  {
    key: "uppercase",
    label: "Contains an uppercase letter",
    test: (value) => /[A-Z]/.test(value),
  },
  {
    key: "number",
    label: "Contains a number (0-9)",
    test: (value) => /[0-9]/.test(value),
  },
  {
    key: "special",
    label: "Contains a special character (!@#$...)",
    test: (value) => /[^A-Za-z0-9]/.test(value),
  },
];

const PasswordField = ({
  id,
  label,
  value,
  onChange,
  placeholder,
  error,
  hint,
  showPassword,
  onToggle,
}) => (
  <div className="student-account-settings__field">
    <label htmlFor={id} className="student-account-settings__label">
      {label} <span className="student-account-settings__required">*</span>
    </label>
    <div className="student-account-settings__input-wrap">
      <input
        type={showPassword ? "text" : "password"}
        id={id}
        name={id}
        className="student-account-settings__input"
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        autoComplete={id === "oldPassword" ? "current-password" : "new-password"}
      />
      <button
        type="button"
        className="student-account-settings__toggle"
        onClick={onToggle}
        aria-label={showPassword ? "Hide password" : "Show password"}
      >
        <i className={`fas ${showPassword ? "fa-eye-slash" : "fa-eye"}`} aria-hidden="true" />
      </button>
    </div>
    {hint ? <p className="student-account-settings__hint">{hint}</p> : null}
    {error ? <span className="student-account-settings__error">{error}</span> : null}
  </div>
);

const UpdatePassword = ({ history }) => {
  const alert = useAlert();
  const dispatch = useDispatch();

  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errorData, setErrorData] = useState({});

  const { loading, isUpdated, error, errors } = useSelector((state) => state.user);

  const requirementStatus = useMemo(
    () =>
      PASSWORD_REQUIREMENTS.map((item) => ({
        ...item,
        met: item.test(newPassword),
      })),
    [newPassword]
  );

  useEffect(() => {
    if (errors) {
      setErrorData(errors);
    }

    if (error) {
      alert.error(error);
      dispatch(clearErrors());
    }

    if (isUpdated) {
      alert.success("Password updated successfully");
      history.push("/profile");

      dispatch({
        type: UPDATE_PASSWORD_RESET,
      });
    }
  }, [dispatch, isUpdated, alert, error, errors, history]);

  const submitHandler = (e) => {
    e.preventDefault();
    dispatch(updatePassword(oldPassword, newPassword, confirmPassword));
  };

  return (
    <Fragment>
      {loading ? (
        <Loader />
      ) : (
        <div className="student-account-settings">
          <h1 className="student-account-settings__title">Account Setting</h1>

          <div className="student-account-settings__card">
            <div className="student-account-settings__intro">
              <div className="student-account-settings__intro-icon">
                <i className="fas fa-shield-alt" aria-hidden="true" />
              </div>
              <div>
                <p className="student-account-settings__intro-title">Change Your Password</p>
                <p className="student-account-settings__intro-text">
                  Use a strong password with at least 8 characters, a number, and a special
                  character.
                </p>
              </div>
            </div>

            <form id="updatePasswordForm" onSubmit={submitHandler}>
              <PasswordField
                id="oldPassword"
                label="Old Password"
                value={oldPassword}
                onChange={(e) => setOldPassword(e.target.value)}
                placeholder="Enter your current password"
                error={errorData.oldPassword}
                showPassword={showOldPassword}
                onToggle={() => setShowOldPassword((current) => !current)}
              />

              <div className="student-account-settings__field-row">
                <PasswordField
                  id="newPassword"
                  label="New Password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password"
                  hint="Min. 8 characters"
                  error={errorData.newPassword}
                  showPassword={showNewPassword}
                  onToggle={() => setShowNewPassword((current) => !current)}
                />
                <PasswordField
                  id="confirmPassword"
                  label="Confirm Password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Re-enter new password"
                  error={errorData.confirmPassword}
                  showPassword={showConfirmPassword}
                  onToggle={() => setShowConfirmPassword((current) => !current)}
                />
              </div>

              <div className="student-account-settings__actions">
                <button
                  type="submit"
                  className="student-account-settings__submit"
                  disabled={loading}
                >
                  Update Password
                </button>
              </div>
            </form>
          </div>

          <div className="student-account-settings__requirements">
            <h2 className="student-account-settings__requirements-title">Password Requirements</h2>
            <ul className="student-account-settings__requirements-list">
              {requirementStatus.map((item) => (
                <li
                  key={item.key}
                  className={`student-account-settings__requirement${
                    item.met ? " student-account-settings__requirement--met" : ""
                  }`}
                >
                  <i
                    className={`fas ${item.met ? "fa-check-circle" : "fa-circle"}`}
                    aria-hidden="true"
                  />
                  {item.label}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </Fragment>
  );
};

export default UpdatePassword;
